import {
  Button,
  ButtonGroup,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Segment,
  Separator,
  closeCurrentDialog,
  closeDialog,
  openDialog,
} from 'platform/components';
import {Box, Grid, GridItem, HStack, VStack} from 'platform/foundation';

import {RefObject, useEffect, useState} from 'react';
import {DeepPartial} from 'react-hook-form';

import {defaultTo, isNil, not} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetManufacturerQuery} from '@dms/api/core';
import {
  GetSupplierOrderItemResponse,
  PatchSupplierOrderItemRequest,
  PutSupplierArticlePromptValidationRequest,
  useGetArticleQuery,
  useGetHandlingUnitQuery,
  useGetHandlingUnitsQuery,
  useGetSupplierOrderItemQuery,
  useGetSupplierQuery,
  usePatchSupplierOrderItemMutation,
  usePutSupplierArticlePromptValidationMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {useTenant, handleApiError, logError} from '@dms/shared';
import {getArticleDetailCardPrices, SUPPLIER_ARTICLE_PROMPT} from '@dms/warehouse/shared';
import {SupplierArticlePrompt} from '@dms/warehouse/shared-components';

import {generateHashFromObjects, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {EDIT_SUPPLIER_ORDER_ITEM_DIALOG} from '../constants/editSupplierOrderItemDialog';
import {SupplierOrderItemForm, SupplierOrderItemFormSchema} from '../types/SupplierOrderItemForm';
import {ArticleDetailCard} from './ArticleDetailCard';
import {AvailabilitySegment} from './AvailabilitySegment';
import {DeliveriesSegment} from './DeliveriesSegment';
import {useSupplierOrderItemFormRecalculation} from './hooks/useSupplierOrderItemFormRecalculation';
import {OrderDetailForm} from './OrderDetailForm';
import {RequestDetailForm} from './RequestDetailForm';
import {RequestsSegment} from './RequestsSegment';
import {SupplierArticleForm} from './SupplierArticleForm';

enum Segments {
  AVAILABILITY = 'availability',
  REQUESTS = 'requests',
  DELIVERIES = 'deliveries',
}

interface SupplierOrderItemDetailProps extends RequiredTestIdProps {
  supplierOrderId: string;
  supplierOrderItemId: string;
  onClose?: VoidFunction;
  isReadOnly?: boolean;
  isSavedByRowControlRef?: RefObject<boolean>;
  formRef?: RefObject<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>;
}

export function SupplierOrderItemDetail(props: SupplierOrderItemDetailProps) {
  const [activeSegment, setActiveSegment] = useState<Segments>(Segments.AVAILABILITY);
  const tenant = useTenant();

  const {
    data: supplierOrderItem,
    isLoading: isSupplierOrderItemLoading,
    isError: hasSupplierOrderItemError,
  } = useGetSupplierOrderItemQuery(
    {
      orderId: props.supplierOrderId,
      itemId: props.supplierOrderItemId,
    },
    {skip: isNilOrEmpty(props.supplierOrderItemId)}
  );

  // Fetching article separately to keep refetched data independent from supplierOrderItem
  const {
    data: article,
    isFetching: isArticleFetching,
    refetch: refreshArticle,
  } = useGetArticleQuery(
    {
      warehouseId: supplierOrderItem?.article?.warehouseId as string,
      articleId: supplierOrderItem?.article?.id as string,
    },
    {
      skip:
        isNilOrEmpty(supplierOrderItem?.article?.warehouseId) ||
        isNilOrEmpty(supplierOrderItem?.article?.id),
    }
  );

  const [putSupplierArticlePromptValidation] = usePutSupplierArticlePromptValidationMutation();

  const [patchSupplierOrderItem, {isLoading: isPatchSupplierOrderItemLoading}] =
    usePatchSupplierOrderItemMutation({fixedCacheKey: 'edit-supplier-order-item'});

  const supplierId = supplierOrderItem?.supplierArticle?.supplierId;

  const {data: supplier, isLoading: isSupplierLoading} = useGetSupplierQuery(
    {supplierId: supplierId as string},
    {skip: isNilOrEmpty(supplierId)}
  );

  const {data: handlingUnits, isLoading: areHandlingUnitsLoading} = useGetHandlingUnitsQuery();

  const {data: handlingUnit} = useGetHandlingUnitQuery(
    {id: supplierOrderItem?.article?.handlingUnit as string},
    {skip: isNil(supplierOrderItem?.article?.handlingUnit)}
  );

  const {data: manufacturer, isLoading: isManufacturerLoading} = useGetManufacturerQuery(
    {manufacturerId: supplierOrderItem?.orderDetail?.manufacturerId},
    {skip: isNil(supplierOrderItem?.orderDetail?.manufacturerId)}
  );

  const articleId = supplierOrderItem?.article?.id;

  const manufacturerNumber = supplierOrderItem?.article?.manufacturerNumber;

  const currency = defaultTo(tenant.tenantCurrency, supplierOrderItem?.orderDetail?.currency);

  const articlePrices = getArticleDetailCardPrices(supplierOrderItem?.article);

  const isLoading = isSupplierOrderItemLoading || isSupplierLoading || isManufacturerLoading;

  const defaultValues: DeepPartial<SupplierOrderItemForm> = {
    orderDetail: {
      supplierQuantity: defaultTo(1, supplierOrderItem?.orderDetail?.supplierQuantity),
      supplierUnit: supplierOrderItem?.orderDetail?.supplierUnit,
      supplierUnitPrice: defaultTo(0, supplierOrderItem?.orderDetail?.supplierUnitPrice),
      supplierTotalPrice: defaultTo(0, supplierOrderItem?.orderDetail?.supplierTotalPrice),
      name: supplierOrderItem?.orderDetail?.name,
      catalogueNumber: supplierOrderItem?.orderDetail?.catalogueNumber,
      manufacturerId: supplierOrderItem?.orderDetail?.manufacturerId,
      supplierOrderingNumber: supplierOrderItem?.orderDetail?.supplierOrderingNumber,
      vat: defaultTo('S', supplierOrderItem?.orderDetail?.vat),
      vin: supplierOrderItem?.orderDetail?.vin,
      keyCode: supplierOrderItem?.orderDetail?.keyCode,
      isIntelligentPart: supplierOrderItem?.orderDetail?.isInteligentPart ?? false,
      engineNumber: supplierOrderItem?.orderDetail?.engineNumber,
      labelInfo: supplierOrderItem?.orderDetail?.labelInfo,
      serialNumber: supplierOrderItem?.orderDetail?.serialNumber,
      note: supplierOrderItem?.orderDetail?.note,
      location: supplierOrderItem?.orderDetail?.location,
      engineCode: supplierOrderItem?.orderDetail?.engineCode,
      referenceNumber: supplierOrderItem?.orderDetail?.referenceNumber,
    },
    requestDetail: {
      warehouseRequestedQuantity: defaultTo(
        1,
        supplierOrderItem?.requestDetail?.warehouseRequestedQuantity
      ),
      warehouseUnitPurchasePrice: defaultTo(
        0,
        supplierOrderItem?.requestDetail?.warehouseUnitPurchasePrice
      ),
    },
    supplierArticle: {
      supplierQuantity: defaultTo(1, supplierOrderItem?.supplierArticle?.supplierQuantity),
      supplierUnitId: supplierOrderItem?.supplierArticle?.supplierUnitId,
      warehouseQuantity: defaultTo(1, supplierOrderItem?.supplierArticle?.warehouseQuantity),
      warehouseUnitId: supplierOrderItem?.article?.handlingUnit,
      supplierOrderingNumber: supplierOrderItem?.supplierArticle?.supplierOrderingNumber,
      supplierBulkPackageQuantity: defaultTo(
        1,
        supplierOrderItem?.supplierArticle?.supplierBulkPackageQuantity
      ),
      isUpdateMapping: false,
      supplierDiscountGroupCode: supplierOrderItem?.supplierArticle?.supplierDiscountGroupCode,
    },
  };

  const {handleChange, setPrevFormValues} = useSupplierOrderItemFormRecalculation({
    supplierOrderItemId: props.supplierOrderItemId,
  });

  const handleMapPrevFormValues = (data: GetSupplierOrderItemResponse | Nullish) => {
    const mappedValues = {
      orderDetail: {
        supplierQuantity: defaultTo(1, data?.orderDetail?.supplierQuantity),
        supplierUnit: data?.orderDetail?.supplierUnit,
        supplierUnitPrice: defaultTo(0, data?.orderDetail?.supplierUnitPrice),
        supplierTotalPrice: defaultTo(0, data?.orderDetail?.supplierTotalPrice),
        name: data?.orderDetail?.name,
        catalogueNumber: data?.orderDetail?.catalogueNumber,
        manufacturerId: data?.orderDetail?.manufacturerId,
        supplierOrderingNumber: data?.orderDetail?.supplierOrderingNumber,
        vat: defaultTo('S', data?.orderDetail?.vat),
        vin: data?.orderDetail?.vin,
        keyCode: data?.orderDetail?.keyCode,
        isIntelligentPart: data?.orderDetail?.isInteligentPart,
        labelInfo: data?.orderDetail?.labelInfo,
        serialNumber: data?.orderDetail?.serialNumber,
        note: data?.orderDetail?.note,
        location: data?.orderDetail?.location,
        engineCode: data?.orderDetail?.engineCode,
        referenceNumber: data?.orderDetail?.referenceNumber,
      },
      requestDetail: {
        warehouseRequestedQuantity: defaultTo(1, data?.requestDetail?.warehouseRequestedQuantity),
        warehouseUnitPurchasePrice: defaultTo(0, data?.requestDetail?.warehouseUnitPurchasePrice),
      },
      supplierArticle: {
        supplierQuantity: defaultTo(1, data?.supplierArticle?.supplierQuantity),
        supplierUnitId: data?.supplierArticle.supplierUnitId,
        warehouseQuantity: defaultTo(1, data?.supplierArticle?.warehouseQuantity),
        warehouseUnitId: data?.article?.handlingUnit,
        supplierOrderingNumber: data?.supplierArticle?.supplierOrderingNumber,
        supplierBulkPackageQuantity: defaultTo(
          1,
          data?.supplierArticle?.supplierBulkPackageQuantity
        ),
        isUpdateMapping: false,
        supplierDiscountGroupCode: data?.supplierArticle?.supplierDiscountGroupCode,
      },
    };
    setPrevFormValues(mappedValues);
  };

  useEffect(() => {
    if (isNil(supplierOrderItem)) {
      return;
    }

    // Set the prevFormValues, we need them for proper price calculations
    handleMapPrevFormValues(supplierOrderItem);
  }, [supplierOrderItem]);

  const handleSupplierArticlePromptValidation: FormSubmitHandler<SupplierOrderItemForm> = async (
    formValues
  ) => {
    if (isNilOrEmpty(articleId)) {
      return logError('Missing article id');
    }

    if (isNilOrEmpty(supplierId)) {
      return logError('Missing supplier id');
    }

    if (isNilOrEmpty(manufacturerNumber)) {
      return logError('Missing manufacturer number');
    }

    const supplierArticlePromptValidationRequestPayload: PutSupplierArticlePromptValidationRequest =
      {
        articleId: articleId as string,
        supplierId: supplierId as string,
        manufacturerNumber: manufacturerNumber as string,
        supplierOrderingNumber: formValues.orderDetail.supplierOrderingNumber,
        supplierToWarehouseMapping: {
          supplierQuantity: formValues.supplierArticle.supplierQuantity,
          supplierUnitId: formValues.supplierArticle.supplierUnitId,
          warehouseQuantity: formValues.supplierArticle.warehouseQuantity,
          supplierBulkPackageQuantity: formValues.supplierArticle.supplierBulkPackageQuantity,
        },
      };

    await putSupplierArticlePromptValidation(supplierArticlePromptValidationRequestPayload)
      .unwrap()
      .then((result) => {
        if (not(result.isChanged)) {
          return handleSubmit(formValues);
        }

        // Wrap in a Promise so the submit handler doesn't resolve until the user
        // interacts with the prompt and the mutation completes. This is needed
        // for the row control auto-save to wait for the full save flow.
        return new Promise<void>((resolve, reject) => {
          const submitAndResolve = async (shouldUpdateMapping: boolean) => {
            try {
              await handleSubmit(formValues, shouldUpdateMapping);
              resolve();
            } catch (error) {
              reject(error);
            }
          };

          openDialog(
            <SupplierArticlePrompt
              onDontSave={() => submitAndResolve(false)}
              onSave={() => submitAndResolve(true)}
              data-testid={suffixTestId('supplierArticlePrompt', props)}
            />,
            {
              id: SUPPLIER_ARTICLE_PROMPT,
              size: 'small',
              'data-testid': suffixTestId('dialogs.supplierArticlePrompt', props),
            }
          );
        });
      })
      .catch(handleApiError);
  };

  const handleSubmit = async (formValues: SupplierOrderItemForm, shouldUpdateMapping?: boolean) => {
    if (isNilOrEmpty(articleId)) {
      return logError('Article id is not defined');
    }

    const patchRequest: PatchSupplierOrderItemRequest = {
      orderId: props.supplierOrderId,
      itemId: props.supplierOrderItemId,
      body: {
        ...formValues,
        article: {
          id: articleId as string,
        },
        orderDetail: {
          supplierQuantity: formValues.orderDetail.supplierQuantity,
          supplierUnit: formValues.orderDetail.supplierUnit,
          supplierUnitPrice: formValues.orderDetail.supplierUnitPrice,
          supplierTotalPrice: formValues.orderDetail.supplierTotalPrice,
          supplierOrderingNumber: formValues.orderDetail.supplierOrderingNumber,
          vat: formValues.orderDetail.vat,
          vin: formValues.orderDetail.vin,
          keyCode: formValues.orderDetail.keyCode,
          isIntelligentPart: formValues.orderDetail.isIntelligentPart,
          engineNumber: formValues.orderDetail.engineNumber,
          labelInfo: formValues.orderDetail.labelInfo,
          currency,
          note: formValues.orderDetail.note,
          location: formValues.orderDetail.location,
          engineCode: formValues.orderDetail.engineCode,
        },
        supplierArticle: {
          supplierQuantity: formValues.supplierArticle.supplierQuantity,
          supplierUnitId: formValues.supplierArticle.supplierUnitId,
          warehouseQuantity: formValues.supplierArticle.warehouseQuantity,
          supplierOrderingNumber: formValues.supplierArticle.supplierOrderingNumber,
          supplierBulkPackageQuantity: formValues.supplierArticle.supplierBulkPackageQuantity,
          isUpdateMapping: shouldUpdateMapping ?? false,
        },
      },
    };

    await patchSupplierOrderItem(patchRequest)
      .unwrap()
      .then(() => {
        if (props.isSavedByRowControlRef?.current) {
          props.isSavedByRowControlRef.current = false;
          return;
        }
        props.onClose ? props.onClose() : closeDialog(EDIT_SUPPLIER_ORDER_ITEM_DIALOG);
      })
      .catch(handleApiError);
  };

  const handleDiscard = () => {
    if (props.onClose) {
      props.onClose();
    } else {
      closeCurrentDialog();
    }
  };

  const segments = [
    {
      value: Segments.AVAILABILITY,
      label: i18n.t('entity.warehouse.labels.availability'),
      content: (
        <AvailabilitySegment
          manufacturerId={supplierOrderItem?.orderDetail?.manufacturerId}
          manufacturerNumber={supplierOrderItem?.orderDetail?.catalogueNumber}
          data-testid={suffixTestId('segments.availability', props)}
        />
      ),
    },
    {
      value: Segments.REQUESTS,
      label: i18n.t('entity.warehouse.labels.requests'),
      content: (
        <RequestsSegment
          orderItemId={props.supplierOrderItemId}
          data-testid={suffixTestId('segments.requests', props)}
        />
      ),
    },
    {
      value: Segments.DELIVERIES,
      label: i18n.t('entity.warehouse.labels.deliveries'),
      content: (
        <DeliveriesSegment
          orderItemId={props.supplierOrderItemId}
          data-testid={suffixTestId('segments.deliveries', props)}
        />
      ),
    },
  ];

  const segmentContent = segments.find((segment) => segment.value === activeSegment)?.content;

  return (
    <Form<SupplierOrderItemForm>
      key={generateHashFromObjects(supplierOrderItem)}
      experimentalZodSchema={SupplierOrderItemFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSupplierArticlePromptValidation}
      isDisabled={props.isReadOnly}
    >
      {(control, formApi) => {
        const formRef = props.formRef;
        if (formRef) {
          formRef.current = {
            submit: () => control.submitForm(),
            isDirty: formApi.formState.isDirty,
          };
        }

        // For controlling recalculations of prices
        formApi.watch((data, change) => handleChange(data, change, formApi));

        return (
          <VStack spacing={4}>
            <DataStatus isLoading={isLoading} isError={hasSupplierOrderItemError} minHeight={290}>
              <Grid columns={4}>
                <GridItem span={1}>
                  <FormField
                    isDisabled
                    control={control}
                    name="orderDetail.serialNumber"
                    type="text"
                    label={i18n.t('entity.warehouse.labels.serialNumber')}
                    data-testid={suffixTestId('inputs.serialNumber', props)}
                  />
                </GridItem>
              </Grid>

              <OrderDetailForm
                control={control}
                formApi={formApi}
                manufacturer={manufacturer?.name}
                handlingUnit={handlingUnit?.name}
                handlingUnits={handlingUnits}
                areHandlingUnitsLoading={areHandlingUnitsLoading}
                currency={currency}
                data-testid={suffixTestId('sections.orderDetail', props)}
              />

              <Separator spacing={0} />

              <RequestDetailForm
                control={control}
                handlingUnit={handlingUnit?.name}
                currency={currency}
                data-testid={suffixTestId('sections.requestDetail', props)}
              />

              <Separator spacing={0} />

              <SupplierArticleForm
                control={control}
                handlingUnit={handlingUnit?.name}
                handlingUnits={handlingUnits}
                areHandlingUnitsLoading={areHandlingUnitsLoading}
                supplierName={supplier?.name}
                data-testid={suffixTestId('sections.supplierArticle', props)}
              />

              <ArticleDetailCard
                articleDetail={{
                  warehouseId: article?.warehouseId,
                  articleId: article?.id,
                  name: article?.name,
                  manufacturerNumber: article?.manufacturerNumber,
                  manufacturerId: article?.manufacturerId,
                  storageLocation: article?.storageLocation,
                  leafId: article?.treeFolder?.leafId,
                }}
                prices={articlePrices}
                currency={currency}
                isRefreshingArticle={isArticleFetching}
                onRefresh={refreshArticle}
                data-testid={suffixTestId('sections.articleDetailCard', props)}
              />

              <HStack>
                <Segment<Segments>
                  value={activeSegment}
                  onChange={setActiveSegment}
                  options={segments}
                />
              </HStack>

              <Box>{segmentContent}</Box>

              <DialogFooter>
                <ButtonGroup align="right">
                  <Button
                    variant="secondary"
                    title={i18n.t('general.labels.discard')}
                    onClick={handleDiscard}
                    isDisabled={props.isReadOnly}
                    data-testid={suffixTestId('actions.discard', props)}
                  />
                  <FormButton
                    control={control}
                    type="submit"
                    variant="primary"
                    title={i18n.t('general.labels.saveChanges')}
                    isDisabled={isPatchSupplierOrderItemLoading}
                    data-testid={suffixTestId('actions.submit', props)}
                  />
                </ButtonGroup>
              </DialogFooter>
            </DataStatus>
          </VStack>
        );
      }}
    </Form>
  );
}
